import { EpisodeStatusTag } from '@/components/episodes/EpisodeStatusTag';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Collapse, IconButton, Typography } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { tz } from 'moment-timezone';
import { useMemo, useState } from 'react';

import { isOrderDiscontinued } from '../../../types/order';
import {
    getEpisodeLabel,
    getEpisodeNotesDescription,
    getEpisodeNotesHeadline,
    getEpisodeNotesLabel,
} from '../../../utils/episodeLabel';
import { OneLineDateRangeDisplay } from '../../dates/OneLineDateRangeDisplay';
import { NoneLabel } from '../../labels/NoneLabel';
import { NotesEditor } from '../../notes/NotesEditor';
import { renderOrderFrequency1xStyle } from '../../orders/labels';
import {
    HeadingCaptionLabel,
    LeftRailDiscontinuedLabel,
    LeftRailNoneLabel,
    LeftRailValueTypography,
    valueTypographyProps,
} from './PatientLeftRail';
import { PatientLeftRailEpisodeFragment } from './PatientLeftRailEpisodeSection.fragments.gql.types';

type FrequencyOrderItem = NonNullable<
    PatientLeftRailEpisodeFragment['frequencyOrders']
>['items'][number];
type PrnOrderItem = NonNullable<PatientLeftRailEpisodeFragment['prnOrders']>['items'][number];

type EpisodeOrder = FrequencyOrderItem | PrnOrderItem;

type FrequencyOrder = FrequencyOrderItem & {
    frequency: NonNullable<FrequencyOrderItem['frequency']>;
};

type PrnOrder = PrnOrderItem & {
    prn: NonNullable<PrnOrderItem['prn']>;
};

const isFrequencyOrder = (order: EpisodeOrder): order is FrequencyOrder => {
    return 'frequency' in order && order.frequency !== null;
};

const isPrnOrder = (order: EpisodeOrder): order is PrnOrder => {
    return 'prn' in order && order.prn !== null;
};

const EpisodeDateRangeDisplay = ({
    startDate,
    endDate,
}: {
    startDate: Date | null;
    endDate: Date | null;
}) => {
    if (startDate && endDate) {
        return (
            <OneLineDateRangeDisplay
                startDate={startDate}
                endDate={endDate}
                timezone={tz.guess()}
                dateProps={valueTypographyProps}
            />
        );
    }
    if (startDate) {
        return (
            <LeftRailValueTypography>
                {moment(startDate).format('MM/DD/YYYY')}
            </LeftRailValueTypography>
        );
    }
    if (endDate) {
        return (
            <LeftRailValueTypography>
                {moment(endDate).format('MM/DD/YYYY')}
            </LeftRailValueTypography>
        );
    }

    return <NoneLabel />;
};

const OneLineFrequencyWithDiscipline = ({ order }: { order: FrequencyOrder }) => {
    return (
        <Typography fontWeight='400' fontSize='14px' lineHeight='20px' color='#757575'>
            {`${order.discipline?.code} ${renderOrderFrequency1xStyle({
                frequency: order.frequency,
                withIntervals: true,
            })}`.trim()}
        </Typography>
    );
};

const OneLinePrnWithDiscipline = ({
    order: { discipline, prn, numActiveOccurrences },
}: {
    order: PrnOrder;
}) => {
    return (
        <LeftRailValueTypography>
            {`${discipline?.code} PRN (${numActiveOccurrences} out of ${prn.authorizedVisitCount})`.trim()}
        </LeftRailValueTypography>
    );
};

const EDIT_EPISODE_NOTES_MUTATION = gql`
    mutation EditEpisodeNotes($episodeId: UUID!, $notes: NonEmptyString) {
        editEpisodeNotes(input: { episodeId: $episodeId, notes: $notes }) {
            id
            notes
        }
    }
`;

const PatientEpisodeNotes = ({ episode }: { episode: PatientLeftRailEpisodeFragment }) => {
    const { id, notes, permissions } = episode;
    const [editEpisodeNotes] = useMutation(EDIT_EPISODE_NOTES_MUTATION);

    const handleSave = async (notesValue: string) => {
        await editEpisodeNotes({
            variables: {
                episodeId: id,
                notes: notesValue.trim() || null,
            },
        });
    };

    return (
        <NotesEditor
            notes={notes}
            canView={true}
            canEdit={permissions.editNotes}
            onSave={handleSave}
            notesInfoIconProps={{
                visibleToClinicians: true,
                headline: getEpisodeNotesHeadline(episode.typeLabel),
                description: getEpisodeNotesDescription(episode.typeLabel),
            }}
            captionLabel={
                <HeadingCaptionLabel>{getEpisodeNotesLabel(episode.typeLabel)}</HeadingCaptionLabel>
            }
            transformNotesBeforeSave={(notesValue) => notesValue.trim() || null}
        />
    );
};

export const PatientLeftRailEpisodeSection = ({
    episode,
    ordersEnabled,
}: {
    episode: PatientLeftRailEpisodeFragment;
    ordersEnabled: boolean;
}) => {
    const [isFrequenciesExpanded, setIsFrequenciesExpanded] = useState(true);
    const showOrderSection =
        ordersEnabled && (episode.frequencyOrders !== null || episode.prnOrders !== null);
    const [activeOrders, discontinuedOrders] = useMemo(() => {
        const orders: EpisodeOrder[] = [
            ...(episode.frequencyOrders?.items ?? []),
            ...(episode.prnOrders?.items ?? []),
        ];
        // Match the order chip: an order is only "discontinued" once its
        // discontinuedAt date has passed (future-dated orders are still active).
        return _.partition(orders, (o) => !isOrderDiscontinued(o.discontinuedAt));
    }, [episode.frequencyOrders, episode.prnOrders]);

    return (
        <>
            <VerticalStack spacing={0.5}>
                <HorizontalStack justifyContent='flex-start' alignItems='center' gap={1}>
                    <HeadingCaptionLabel>
                        {getEpisodeLabel(episode.typeLabel, episode.numberInSequence)}
                    </HeadingCaptionLabel>
                    <EpisodeStatusTag status={episode.status} />
                </HorizontalStack>
                <EpisodeDateRangeDisplay startDate={episode.startDate} endDate={episode.endDate} />
            </VerticalStack>
            <PatientEpisodeNotes episode={episode} />
            {showOrderSection ? (
                <VerticalStack spacing={0.5}>
                    <HorizontalStack justifyContent='space-between' alignItems='center'>
                        <HeadingCaptionLabel>Frequencies</HeadingCaptionLabel>
                        <IconButton
                            onClick={() => setIsFrequenciesExpanded((prev) => !prev)}
                            size='small'
                            aria-label={
                                isFrequenciesExpanded
                                    ? 'Collapse frequencies'
                                    : 'Expand frequencies'
                            }
                            aria-expanded={isFrequenciesExpanded}
                        >
                            {isFrequenciesExpanded ? (
                                <ExpandLess sx={{ color: '#14AE5C' }} />
                            ) : (
                                <ExpandMore sx={{ color: '#14AE5C' }} />
                            )}
                        </IconButton>
                    </HorizontalStack>
                    <Collapse in={isFrequenciesExpanded}>
                        <VerticalStack spacing={0.5}>
                            <HeadingCaptionLabel fontWeight='400'>Current</HeadingCaptionLabel>
                            <VerticalStack spacing={0.5} sx={{ pt: 1.0 }}>
                                {activeOrders.length > 0 ? (
                                    activeOrders.map((order) => {
                                        if (isFrequencyOrder(order)) {
                                            return (
                                                <OneLineFrequencyWithDiscipline
                                                    key={order.id}
                                                    order={order}
                                                />
                                            );
                                        }
                                        if (isPrnOrder(order)) {
                                            return (
                                                <OneLinePrnWithDiscipline
                                                    key={order.id}
                                                    order={order}
                                                />
                                            );
                                        }
                                        return null;
                                    })
                                ) : (
                                    <LeftRailNoneLabel text='No frequencies' />
                                )}
                            </VerticalStack>
                            {discontinuedOrders.length > 0 && (
                                <>
                                    <HeadingCaptionLabel color='text.disabled' sx={{ pt: 1.0 }}>
                                        Discontinued
                                    </HeadingCaptionLabel>
                                    <VerticalStack spacing={0.5} sx={{ pt: 1.0 }}>
                                        {discontinuedOrders.map((order) => {
                                            if (isFrequencyOrder(order)) {
                                                return (
                                                    <LeftRailDiscontinuedLabel
                                                        key={order.id}
                                                        text={`${
                                                            order.discipline?.code
                                                        } ${renderOrderFrequency1xStyle({
                                                            frequency: order.frequency,
                                                            withIntervals: true,
                                                        })}`.trim()}
                                                    />
                                                );
                                            }
                                            if (isPrnOrder(order)) {
                                                return (
                                                    <LeftRailDiscontinuedLabel
                                                        key={order.id}
                                                        text={`${order.discipline?.code} PRN (${order.numActiveOccurrences} out of ${order.prn.authorizedVisitCount})`.trim()}
                                                    />
                                                );
                                            }
                                            return null;
                                        })}
                                    </VerticalStack>
                                </>
                            )}
                        </VerticalStack>
                    </Collapse>
                </VerticalStack>
            ) : null}
        </>
    );
};
